import type { ReactNode } from 'react';
import { numero } from '../formato';
import { Carregando } from '../ui';
import type { Indicador } from './tipos';

/**
 * O Andamento: o período e os números dele (RN-256, 5).
 *
 * O período é 7 · 30 · 90 dias (quem monta o painel começa em 30 e guarda
 * a escolha no endereço, `?dias=`). Cada indicador diz o número, a
 * comparação com o período anterior ESCRITA em palavra («cresceu», «2 dias
 * a menos») — o tom (bom, ruim, neutro) só reforça com a cor — e, quando há
 * série, o minigráfico logo embaixo do número (M4).
 *
 * Enquanto os números chegam (ao abrir, ou depois de trocar o período), a
 * peça CONTINUA na tela: os botões do período ficam onde estavam — e com
 * eles o foco de quem tocou «90 dias» —, e só o lugar dos números diz
 * «carregando» (`aria-busy`). Desmontar tudo e montar de novo jogava o foco
 * no corpo da página (revisão do ramo). Um erro, quando não há número
 * nenhum para mostrar, também vai no lugar dos números (`aviso`).
 */

/** Os períodos do Andamento, em dias — os mesmos para todo painel de área. */
export const PERIODOS = [7, 30, 90] as const;
type Dias = typeof PERIODOS[number];

export function IndicadoresDoPeriodo({ indicadores, dias, aoDias, carregando = false, aviso }: {
  indicadores: Indicador[];
  dias: number;
  aoDias(n: Dias): void;
  /** Os números do período escolhido ainda não chegaram. */
  carregando?: boolean;
  /** O que dizer no lugar dos números quando eles não vieram (o erro). */
  aviso?: ReactNode;
}) {
  return (
    <div className="pn-indicadores">
      <div className="pn-periodo">
        <div className="pn-periodos" role="group" aria-label="Período">
          {PERIODOS.map((n) => (
            <button key={n} type="button" className="pn-dias" aria-pressed={dias === n}
              onClick={() => aoDias(n)}>
              {n} dias
            </button>
          ))}
        </div>
        <p className="pn-periodo-sub">comparado com os {dias} dias anteriores</p>
      </div>

      <div className="pn-kpis" aria-busy={carregando}>
        {carregando ? <Carregando texto="Montando o andamento" /> : aviso ?? indicadores.map((i) => (
          <article key={i.chave} className="pn-kpi" data-chave={i.chave}>
            <h3 className="pn-kpi-rot">{i.rotulo}</h3>
            <p className="pn-kpi-valor">
              <span className="pn-kpi-num">{i.valor}</span>
              {i.unidade && <span className="pn-kpi-uni">{i.unidade}</span>}
            </p>
            {i.serie && i.serie.length >= 2 && <Faisca serie={i.serie} />}
            {i.detalhe && <p className="pn-kpi-det">{i.detalhe}</p>}
            {i.comparacao && (
              <p className="pn-comparacao" data-tom={i.comparacao.tom}>{i.comparacao.texto}</p>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}

/** O desenho do minigráfico: 120 × 32, com folga para o ponto final. */
const LARGURA = 120;
const ALTURA = 32;
const FOLGA = 4;

/**
 * O minigráfico (M4): a série inteira numa linha, o último ponto marcado.
 *
 * Um ponto sem valor (uma semana sem laudo, por exemplo) interrompe a
 * linha em vez de virar zero: um zero inventado desenharia uma queda que
 * não aconteceu. O leitor de tela recebe o que a linha é — um ponto por
 * semana (RN-256, 5), as 8 últimas — e em que ordem os números vêm, antes
 * dos números por extenso: sem isso, «2, −1, 0…» não diz nada.
 */
function Faisca({ serie }: { serie: number[] }) {
  const validos = serie.filter((v) => typeof v === 'number' && Number.isFinite(v));
  if (validos.length < 2) return null;
  const min = Math.min(...validos);
  const max = Math.max(...validos);
  const faixa = max - min || 1;
  const x = (i: number) => FOLGA + (i * (LARGURA - 2 * FOLGA)) / (serie.length - 1);
  const y = (v: number) => (max === min
    ? ALTURA / 2
    : ALTURA - FOLGA - ((v - min) * (ALTURA - 2 * FOLGA)) / faixa);

  let caminho = '';
  let levantado = true;
  serie.forEach((v, i) => {
    if (typeof v !== 'number' || !Number.isFinite(v)) { levantado = true; return; }
    caminho += `${levantado ? 'M' : 'L'}${x(i).toFixed(1)} ${y(v).toFixed(1)} `;
    levantado = false;
  });

  let ultimo = serie.length - 1;
  while (ultimo >= 0 && !(typeof serie[ultimo] === 'number' && Number.isFinite(serie[ultimo]))) ultimo -= 1;
  /* O número lido pelo formatador da casa (P-FMT-01): vírgula decimal e
     até três casas, sem zeros à direita — o que o leitor de tela ouve. */
  const leitura = serie.map((v) => (Number.isFinite(v) ? numero(v, 3, true) : '—')).join(', ');
  const rotulo = `Tendência das últimas ${serie.length} semanas, da mais antiga à mais nova: ${leitura}`;

  return (
    <svg className="pn-faisca" viewBox={`0 0 ${LARGURA} ${ALTURA}`} role="img"
      aria-label={rotulo} preserveAspectRatio="none">
      <path className="pn-faisca-linha" d={caminho.trim()} />
      <circle className="pn-faisca-ponto" cx={x(ultimo)} cy={y(serie[ultimo])} r="3" />
    </svg>
  );
}
